/**
 * Browser-side external URL opener for WebUI and Viewer.
 *
 * window.open(url, '_blank', 'noopener,noreferrer') is unreliable for non-http
 * schemes in cross-origin HTTPS contexts: Chrome can route them as rewritten
 * same-origin paths instead of triggering the OS external protocol handler.
 * A real anchor click goes through browser link navigation and preserves
 * external protocol dispatch.
 */

import {
  classifyExternalUrl,
  type UrlClassification,
} from '@okclawpro/shared/utils/url-safety'

export type OpenExternalUrlResult =
  | { opened: true }
  | { opened: false; reason: 'dangerous'; detail: string }
  | { opened: false; reason: 'internal-deeplink' }
  | { opened: false; reason: 'malformed' }

export function openExternalUrl(rawUrl: string): OpenExternalUrlResult {
  const classification: UrlClassification = classifyExternalUrl(rawUrl)

  if (classification.kind === 'dangerous') {
    return { opened: false, reason: 'dangerous', detail: classification.reason }
  }

  if (classification.kind === 'internal-deeplink') {
    return { opened: false, reason: 'internal-deeplink' }
  }

  const url = rawUrl.trim()

  let parsed: URL
  try {
    parsed = new URL(url)
  } catch {
    return { opened: false, reason: 'malformed' }
  }

  if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
    window.open(url, '_blank', 'noopener,noreferrer')
    return { opened: true }
  }

  const anchor = document.createElement('a')
  anchor.href = url
  anchor.target = '_blank'
  anchor.rel = 'noopener noreferrer'
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  return { opened: true }
}
